import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Check, Star, Shield, Clock, Zap, ArrowRight, Gift, Heart, Truck, Package, Users, Percent, Play, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  Star, Shield, Clock, Zap, Check, Heart, Truck, Package, Users, Percent, Gift, ArrowRight, Image
};

// Fallback templates for backwards compatibility
const fallbackTemplates = {
  'new-year-sale': {
    headline: 'New Year, New Savings',
    subheadline: 'Start 2026 Right with Up to $500 Off',
    description: 'Ring in the new year with our biggest sale ever. Get a premium D-Freeze freeze dryer and start preserving food for decades.',
    cta_text: 'Shop the Sale',
    cta_link: '/products',
    bg_color: 'from-[#003456] to-[#002B47]',
    badge_text: 'Limited Time Offer',
    benefits: [
      { icon: 'Shield', title: '5-Year Warranty', description: 'Industry-leading coverage on all models' },
      { icon: 'Clock', title: '25+ Year Shelf Life', description: 'Preserve food for decades, not months' },
      { icon: 'Zap', title: 'Energy Efficient', description: 'Smart power management keeps costs low' },
      { icon: 'Star', title: 'Made in USA', description: 'Quality craftsmanship you can trust' }
    ],
    offers: [
      { name: 'D-Freeze Standard', original_price: 2495, sale_price: 2195, savings: 300 },
      { name: 'D-Freeze Pro', original_price: 3495, sale_price: 2995, savings: 500 },
      { name: 'D-Freeze Commercial', original_price: 4995, sale_price: 4595, savings: 400 }
    ],
    urgency_text: 'Sale ends January 31st!',
    show_email_signup: true,
    email_headline: "Don't Miss Out",
    email_description: 'Subscribe to get exclusive offers, tips, and updates delivered to your inbox.'
  },
  'financing': {
    headline: 'As Low As 0% APR Financing',
    subheadline: 'As Low as $67/Month',
    description: 'Get the freeze dryer you want today with easy monthly payments. No hidden fees, no surprises.',
    cta_text: 'Apply Now',
    cta_link: '/financing',
    bg_color: 'from-[#2d5a27] to-[#1a3517]',
    badge_text: 'Easy Approval',
    benefits: [
      { icon: 'Check', title: 'No Interest', description: 'As low as 0% APR for qualified buyers' },
      { icon: 'Clock', title: 'Flexible Terms', description: 'Choose 12, 24, or 36 month plans' },
      { icon: 'Shield', title: 'No Hidden Fees', description: 'What you see is what you pay' },
      { icon: 'Zap', title: 'Quick Approval', description: 'Get approved in minutes' }
    ],
    offers: [
      { name: 'D-Freeze Standard', original_price: 2495, monthly_price: 69 },
      { name: 'D-Freeze Pro', original_price: 3495, monthly_price: 97 },
      { name: 'D-Freeze Commercial', original_price: 4995, monthly_price: 139 }
    ],
    urgency_text: 'Apply today - approval in minutes!',
    show_email_signup: true
  }
};

const MarketingLandingPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Helper to extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try to fetch from database first
        const res = await axios.get(`${API_URL}/api/landing-pages/public/${slug}`);
        setPageData(res.data);
        
        // Track page view
        if (res.data?.id) {
          const visitorId = localStorage.getItem('visitor_id') || null;
          const trackRes = await axios.post(`${API_URL}/api/landing-pages/${res.data.id}/track-view`, null, {
            params: { visitor_id: visitorId }
          });
          if (trackRes.data?.visitor_id && !localStorage.getItem('visitor_id')) {
            localStorage.setItem('visitor_id', trackRes.data.visitor_id);
          }
        }
      } catch (error) {
        // Fall back to hardcoded templates
        if (fallbackTemplates[slug]) {
          setPageData(fallbackTemplates[slug]);
        } else {
          setPageData(fallbackTemplates['new-year-sale']);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products`);
        setProducts(res.data.filter(p => p.category === 'freeze-dryer'));
      } catch (error) {
        console.error('Error fetching products');
      }
    };

    fetchData();
    fetchProducts();
  }, [slug]);

  // Track conversion when CTA is clicked
  const trackConversion = async (conversionType = 'cta_click') => {
    if (pageData?.id) {
      try {
        const visitorId = localStorage.getItem('visitor_id') || null;
        await axios.post(`${API_URL}/api/landing-pages/${pageData.id}/track-conversion`, null, {
          params: { conversion_type: conversionType, visitor_id: visitorId }
        });
      } catch (error) {
        console.error('Error tracking conversion');
      }
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        interests: ['sales', 'new-products'],
        source: `landing-${slug}`
      });
      toast.success('Thanks for subscribing! Check your email for exclusive offers.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003456]"></div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Page Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="marketing-landing">
      <Navbar />
      
      {/* Hero Section */}
      <section className={`relative py-24 md:py-32 ${pageData.hero_image_url ? '' : `bg-gradient-to-br ${pageData.bg_color || 'from-[#003456] to-[#002B47]'}`} overflow-hidden`}>
        {/* Background Image */}
        {pageData.hero_image_url && (
          <div className="absolute inset-0">
            <img 
              src={pageData.hero_image_url} 
              alt={pageData.headline}
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${pageData.bg_color || 'from-[#003456] to-[#002B47]'} opacity-80`}></div>
          </div>
        )}
        
        {/* Decorative blurs (only if no hero image) */}
        {!pageData.hero_image_url && (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
        )}
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className={`${pageData.hero_video_url || (pageData.gallery_images && pageData.gallery_images.length > 0) ? 'grid md:grid-cols-2 gap-12 items-center' : 'text-center'}`}>
            {/* Content Column */}
            <div className={`${pageData.hero_video_url || (pageData.gallery_images && pageData.gallery_images.length > 0) ? '' : 'max-w-3xl mx-auto'}`}>
              {pageData.badge_text && (
                <span className="inline-block px-4 py-2 bg-[#CE8946] text-[#0a0203] rounded-full text-sm font-semibold mb-6 animate-pulse">
                  <Gift className="inline h-4 w-4 mr-2" />
                  {pageData.badge_text}
                </span>
              )}
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6" data-testid="landing-headline">
                {pageData.headline}
              </h1>
              
              {pageData.subheadline && (
                <p className="text-2xl md:text-3xl text-[#CE8946] font-semibold mb-4">
                  {pageData.subheadline}
                </p>
              )}
              
              {pageData.description && (
                <p className="text-xl text-white/80 mb-8">
                  {pageData.description}
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 justify-start">
                <Link to={pageData.cta_link || '/products'} onClick={() => trackConversion('cta_click')}>
                  <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-8 py-6 text-lg" data-testid="landing-cta">
                    {pageData.cta_text || 'Shop Now'} <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                {pageData.secondary_cta_text && (
                  <Link to={pageData.secondary_cta_link || '/contact'} onClick={() => trackConversion('secondary_cta_click')}>
                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                      {pageData.secondary_cta_text}
                    </Button>
                  </Link>
                )}
              </div>

              {pageData.urgency_text && (
                <p className="mt-8 text-white/70 text-sm">
                  <Clock className="inline h-4 w-4 mr-1" />
                  {pageData.urgency_text}
                </p>
              )}
            </div>

            {/* Media Column - Video or Gallery */}
            {(pageData.hero_video_url || (pageData.gallery_images && pageData.gallery_images.length > 0)) && (
              <div className="relative">
                {pageData.hero_video_url ? (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black/20">
                    {getYouTubeId(pageData.hero_video_url) ? (
                      <>
                        <img 
                          src={`https://img.youtube.com/vi/${getYouTubeId(pageData.hero_video_url)}/maxresdefault.jpg`}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                        <button 
                          onClick={() => setShowVideoModal(true)}
                          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                        >
                          <div className="w-20 h-20 bg-[#CE8946] rounded-full flex items-center justify-center">
                            <Play className="h-10 w-10 text-[#0a0203] ml-1" fill="currentColor" />
                          </div>
                        </button>
                      </>
                    ) : (
                      <video 
                        src={pageData.hero_video_url}
                        controls
                        className="w-full h-full object-cover"
                        poster={pageData.hero_image_url}
                      />
                    )}
                  </div>
                ) : pageData.gallery_images && pageData.gallery_images.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {pageData.gallery_images.slice(0, 4).map((img, idx) => (
                      <div key={idx} className={`rounded-xl overflow-hidden shadow-lg ${idx === 0 ? 'col-span-2' : ''}`}>
                        <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && pageData.hero_video_url && getYouTubeId(pageData.hero_video_url) && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative w-full max-w-4xl aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeId(pageData.hero_video_url)}?autoplay=1`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full rounded-xl"
            />
            <button 
              className="absolute -top-12 right-0 text-white text-lg"
              onClick={() => setShowVideoModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      {pageData.benefits && pageData.benefits.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">
                Why Choose D-Freeze?
              </h2>
            </div>
            
            <div className={`grid md:grid-cols-2 lg:grid-cols-${Math.min(pageData.benefits.length, 4)} gap-8`}>
              {pageData.benefits.map((benefit, i) => {
                const Icon = getIcon(benefit.icon);
                return (
                  <div key={i} className="text-center p-6 rounded-2xl bg-[#F8FAFC] hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 mx-auto bg-[#003456] rounded-2xl flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-[#CE8946]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0a0203] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Products/Offers Section */}
      {((pageData.offers && pageData.offers.length > 0) || (pageData.show_products && products.length > 0)) && (
        <section className="py-20 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">
                {pageData.offers && pageData.offers.length > 0 ? 'Special Offers' : 'Our Products'}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {(pageData.offers && pageData.offers.length > 0 ? pageData.offers : products.slice(0, 3)).map((item, i) => {
                // Get product image - handle both offer objects and product objects
                const productImage = item.image_url || item.images?.[0] || (item.image ? item.image : null);
                
                return (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                    {/* Product Image */}
                    {productImage && (
                      <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                        <img 
                          src={productImage} 
                          alt={item.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="p-8">
                      <h3 className="text-2xl font-bold text-[#0a0203] mb-4">{item.name}</h3>
                      
                      {item.savings ? (
                        <>
                          <div className="flex items-baseline gap-3 mb-2">
                            <span className="text-3xl font-bold text-[#003456]">${item.sale_price?.toLocaleString()}</span>
                            <span className="text-xl text-gray-400 line-through">${item.original_price?.toLocaleString()}</span>
                          </div>
                          <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
                            Save ${item.savings}
                          </span>
                        </>
                      ) : item.monthly_price ? (
                        <>
                          <div className="flex items-baseline gap-2 mb-2">
                            <span className="text-3xl font-bold text-[#003456]">${item.monthly_price}</span>
                            <span className="text-gray-500">/month</span>
                          </div>
                          <p className="text-gray-500 mb-4">or ${item.original_price?.toLocaleString()} total</p>
                        </>
                      ) : (
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-3xl font-bold text-[#003456]">${(item.price || item.original_price)?.toLocaleString()}</span>
                        </div>
                      )}
                      
                      <Link to={item.link || (item.id ? `/products/${item.id}` : '/products')}>
                        <Button className="w-full bg-[#003456] hover:bg-[#002B47]">
                          View Details <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Email Signup Section */}
      {pageData.show_email_signup !== false && (
        <section className="py-20 bg-[#003456]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {pageData.email_headline || "Don't Miss Out"}
            </h2>
            <p className="text-white/70 mb-8">
              {pageData.email_description || 'Subscribe to get exclusive offers, tips, and updates delivered to your inbox.'}
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="landing-email-input"
              />
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold"
                data-testid="landing-subscribe-btn"
              >
                {submitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default MarketingLandingPage;
